import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, IconButton, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchPlaceCategory, deletePlaceCategoryCriteriaValue } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function PlaceCategoryClassificationCriteriaListPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/place-categories/${categoryId}`;

  const [categoryName, setCategoryName] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetRow, setTargetRow] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchPlaceCategory(categoryId);
      setCategoryName(d.placeCategoryName || "");
      const values = (d.classificationCriteriaValues || []).map((v, i) => ({
        id: i,
        criteriaName: v.classificationCriteria?.classificationCriteriaName || "",
        valueName: v.classificationCriteriaValueName || "",
        raw: v,
      }));
      setRows(values);
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
  }, [categoryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeleteClick = (row) => {
    setTargetRow(row);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!targetRow) return;
    try {
      await deletePlaceCategoryCriteriaValue(categoryId, targetRow.raw);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
    setTargetRow(null);
  };

  const filteredRows = searchText
    ? rows.filter((r) => r.criteriaName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "criteriaName", headerName: t("itCategory.classificationCriteriaName"), flex: 1, minWidth: 180 },
    { field: "valueName", headerName: t("itCategory.classificationCriteriaValueName"), flex: 1, minWidth: 180 },
    {
      field: "actions", headerName: t("common.actions"), width: 80, sortable: false,
      renderCell: (params) => (
        <IconButton color="error" size="small" onClick={() => handleDeleteClick(params.row)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(basePath)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">
            {categoryName}：{t("placeCategory.classificationCriteriaValues")}
          </Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/classification-criteria/add`)}>
          {t("common.add")}
        </Button>
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        loading={loading}
        autoHeight
        pageSizeOptions={[100]}
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        disableRowSelectionOnClick
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>
      <ConfirmDialog open={confirmOpen} onConfirm={handleDeleteConfirm} onCancel={() => { setConfirmOpen(false); setTargetRow(null); }} />
    </Box>
  );
}
